<template>
  <div
    class="collapsible-textarea-field"
    :class="{
      'collapsible-textarea-field--card': isExpanded && variant === 'card',
      'collapsible-textarea-field--plain': variant === 'plain',
      'collapsible-textarea-field--error': hasError,
    }"
    :data-testid="dataTestId"
  >
    <div
      v-if="!isExpanded"
      class="collapsible-textarea-field__collapsed d-flex align-center justify-space-between"
      :data-testid="`${dataTestId}__collapsed`"
    >
      <span
        class="collapsible-textarea-field__label"
        :class="{ 'collapsible-textarea-field__label--required': required }"
      >{{ label }}</span>
      <v-btn
        icon
        small
        class="collapsible-textarea-field__expand-btn"
        :aria-label="`Add ${label}`"
        :data-testid="`${dataTestId}__expand-btn`"
        @click="setExpanded(true)"
      >
        <v-icon small>mdi-plus</v-icon>
      </v-btn>
    </div>

    <div
      v-else
      class="collapsible-textarea-field__expanded"
      :data-testid="`${dataTestId}__expanded`"
    >
      <div class="collapsible-textarea-field__header d-flex align-center justify-space-between">
        <span
          class="collapsible-textarea-field__label"
          :class="{ 'collapsible-textarea-field__label--required': required }"
        >{{ label }}</span>
        <v-btn
          v-if="collapsible"
          icon
          small
          class="collapsible-textarea-field__collapse-btn"
          :aria-label="`Collapse ${label}`"
          :data-testid="`${dataTestId}__collapse-btn`"
          @click="setExpanded(false)"
        >
          <v-icon small>mdi-minus</v-icon>
        </v-btn>
      </div>
      <div class="collapsible-textarea-field__input-row">
        <lb-textarea
          :value="value"
          :label="''"
          :rows="rows"
          :placeholder="placeholder"
          :hidedetails="!normalizedError"
          :error="normalizedError"
          class="collapsible-textarea-field__input"
          :class="{ 'collapsible-textarea-field__input--error': !!normalizedError }"
          :data-testid="`${dataTestId}__input`"
          @input="onInput"
        />
      </div>
    </div>
  </div>
</template>

<script>
import './CollapsibleTextAreaField.scss';

/** Collapsible multi-line field for Task Name, Purpose, Instruction (5782 Add Task). */
export default {
  name: 'CollapsibleTextAreaField',

  props: {
    label: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    /** When false, field stays expanded (Task Name). */
    collapsible: {
      type: Boolean,
      default: true,
    },
    /**
     * plain — label above bordered textarea (Task Name)
     * card — bordered expandable card around label + textarea (Purpose / Instruction)
     */
    variant: {
      type: String,
      default: 'card',
      validator: (v) => ['card', 'plain'].includes(v),
    },
    rows: {
      type: [Number, String],
      default: 3,
    },
    error: {
      type: [String, Array],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    dataTestId: {
      type: String,
      default: 'collapsible-textarea-field',
    },
  },

  computed: {
    isExpanded: {
      get() {
        return this.collapsible ? this.expanded : true;
      },
      set(next) {
        if (this.collapsible) {
          this.$emit('update:expanded', next);
        }
      },
    },

    normalizedError() {
      if (this.error == null || this.error === '') {
        return '';
      }
      if (Array.isArray(this.error) && !this.error.length) {
        return '';
      }
      return this.error;
    },

    hasError() {
      const err = this.normalizedError;
      if (!err) return false;
      if (Array.isArray(err)) return err.some((e) => e != null && String(e).trim() !== '');
      return String(err).trim() !== '';
    },

    errorText() {
      const err = this.normalizedError;
      if (Array.isArray(err)) {
        return err.filter((e) => e != null && String(e).trim() !== '').join(', ');
      }
      return String(err);
    },
  },

  methods: {
    setExpanded(next) {
      this.isExpanded = next;
    },

    onInput(nextValue) {
      this.$emit('input', nextValue);
    },
  },
};
</script>
